"use client";

import { Select } from "@tosize/ui";
import { Fragment, useMemo } from "react";
import { P, match } from "ts-pattern";
import { z } from "zod";
import type { SortFields } from "~/graphql/genql/magento";
import { useQuery } from "~/hooks/useQuery";
import { searchParamsSchema } from "../../grid";
import { FilterItemOption } from "../Filter/FilterItemOption";
import { AccordionFilterItem } from "../Filter/FilterOptions";
import { useFilterOption } from "../Filter/useFilterOption";
import type { VariantProps } from "./Options";

interface SortOptions extends VariantProps {
	title: string;
	className?: string;
	sortFields: SortFields;
	ascLabel: string;
	descLabel: string;
}

const SORT_PARAM = "sort";
const DIRECTION_PARAM = "dir";

const directions = ["ASC", "DESC"] as const;

const sortSchema = z.object({
	field: z.string(),
	direction: z.enum(directions),
});

export function SortOptions({
	variant,
	title,
	sortFields,
	ascLabel,
	descLabel,
	...props
}: SortOptions) {
	const { searchParams, router, createUrl } = useQuery();
	const { dir, sort } = searchParamsSchema.parse(Object.fromEntries(searchParams.entries()));
	const value = { direction: dir, field: sort };

	return (
		<Fragment>
			{variant === "stickybar" ? (
				<div className="flex items-center gap-2">
					<span>{title}</span>

					<Select
						size="sm"
						value={JSON.stringify(value)}
						onChange={(event) => {
							try {
								const { direction, field } = sortSchema.parse(JSON.parse(event.target.value));

								router.push(createUrl({ sort: field, dir: direction }));
							} catch {
								// do nothing
							}
						}}
						{...props}
					>
						{sortFields.options.map((option) => (
							<Fragment key={option.value}>
								{directions.map((direction) => (
									<option
										key={direction}
										value={JSON.stringify({ direction, field: option.value })}
									>
										{option.label} (
										{match([direction, option.value])
											.with(["ASC", "name"], () => "A - Z")
											.with(["DESC", "name"], () => "Z - A")
											.with(["ASC", P._], () => ascLabel)
											.with(["DESC", P._], () => descLabel)
											.otherwise(() => "")}
										)
									</option>
								))}
							</Fragment>
						))}
					</Select>
				</div>
			) : (
				<AccordionFilterItem
					value={SORT_PARAM + DIRECTION_PARAM}
					title={title}
					className="xl:hidden"
				>
					{sortFields.options.map((option) => (
						<Fragment key={option.value}>
							{directions.map((direction) => (
								<SortOption
									key={direction}
									value={{ direction, field: option.value }}
									defaultValue={value}
									title={`${option.label} (${match([direction, option.value])
										.with(["ASC", "name"], () => "A - Z")
										.with(["DESC", "name"], () => "Z - A")
										.with(["ASC", P._], () => ascLabel)
										.with(["DESC", P._], () => descLabel)
										.otherwise(() => "")})`}
								/>
							))}
						</Fragment>
					))}
				</AccordionFilterItem>
			)}
		</Fragment>
	);
}

interface SortOptionProps {
	value: { direction: string; field: string };
	defaultValue: { direction?: string; field?: string };
	title: string;
}

function SortOption({ value, defaultValue, title }: SortOptionProps) {
	const { createUrl } = useQuery();
	const { isActive: isSortActive } = useFilterOption({ code: SORT_PARAM, value: value.field });
	const { isActive: isDirActive } = useFilterOption({
		code: DIRECTION_PARAM,
		value: value.direction,
	});

	const active = useMemo(() => {
		if (defaultValue.direction && defaultValue.field) {
			return defaultValue.direction === value.direction && defaultValue.field === value.field;
		}

		return isSortActive && isDirActive;
	}, [
		defaultValue.direction,
		defaultValue.field,
		isDirActive,
		isSortActive,
		value.direction,
		value.field,
	]);

	return (
		<FilterItemOption
			href={createUrl({ sort: value.field, dir: value.direction })}
			title={title}
			isActive={active}
			mode="radio"
		/>
	);
}
